import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Container,
  Box,
  Card,
  CardContent,
  CardHeader,
  Switch,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Snackbar,
  Alert,
  Button,
  Divider,
  LinearProgress,
  TextField,
  Grid,
  CircularProgress,
} from "@mui/material";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import { getSettings, updateSettings, getProfil, setProfil, downloadLegacyBackup } from "../../api";
import { logError } from "../../logging/loggingService";
import { PROFILE_UPDATED_EVENT } from "../../events";
import { useThemeMode } from "../../themeMode";

export default function Settings() {
  const { mode, setMode } = useThemeMode();
  const [settingsData, setSettingsData] = useState({
    mailSettings: {
      smtpHost: "",
      smtpPort: "",
      smtpUsername: "",
      smtpPassword: "",
      smtpSsl: false,
      smtpFromAddress: "",
    },
  });
  const [useCustomMailServer, setUseCustomMailServer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [currentProfil, setCurrentProfil] = useState(null);

  const handleDownloadBackup = async () => {
    setExportLoading(true);
    try {
      const res = await downloadLegacyBackup();
      if (res.status === 200 && res.data) {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.filename || `schreiberlein-backup-${new Date().toISOString().slice(0, 10)}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setSnackbarMessage("Mandanten-Backup erfolgreich heruntergeladen!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        throw new Error(`Export fehlgeschlagen (Status: ${res.status})`);
      }
    } catch (err) {
      logError("Fehler beim Herunterladen des Backups", err, { component: "Settings" });
      setSnackbarMessage("Fehler beim Erstellen der Datensicherung: " + (err.message || "Unbekannter Fehler"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setExportLoading(false);
    }
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, profilRes] = await Promise.all([getSettings(), getProfil()]);

      if (settingsRes.data) {
        setSettingsData(settingsRes.data);
        setUseCustomMailServer(!!settingsRes.data.mailSettings.smtpHost);
      }
      if (profilRes.data) {
        setCurrentProfil(profilRes.data);
      }
    } catch (err) {
      logError("Fehler beim Laden der Einstellungen", err, { component: "Settings" });
      setSnackbarMessage("Einstellungen konnten nicht geladen werden.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleUseCustomMailServerChange = (e) => {
    const isChecked = e.target.checked;
    setUseCustomMailServer(isChecked);
    if (!isChecked) {
      setSettingsData((prev) => ({
        ...prev,
        mailSettings: {
          smtpHost: "",
          smtpPort: "",
          smtpUsername: "",
          smtpPassword: "",
          smtpSsl: false,
          smtpFromAddress: "",
        },
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setSettingsData((prev) => ({
      ...prev,
      mailSettings: {
        ...prev.mailSettings,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    try {
      await updateSettings(settingsData);
      // Save profil specific settings
      if (currentProfil) {
        await setProfil({
          ...currentProfil,
          invoiceCopyByMail: currentProfil.invoiceCopyByMail,
        });
      }
      setSnackbarMessage("Einstellungen erfolgreich gespeichert!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT));
    } catch (error) {
      logError("Fehler beim Speichern der Einstellungen", error, {
        component: "Settings",
      });
      setSnackbarMessage("Fehler beim Speichern der Einstellungen");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(255,165,0,0.12) 0%, rgba(0,0,0,0.06) 100%)",
          py: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Einstellungen
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
            Allgemeine Programmeinstellungen und Benachrichtigungen
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
        {loading && <LinearProgress />}
        {!loading && (
          <Box component="form" onSubmit={handleSubmit}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 8px 28px rgba(0,0,0,0.06)",
                mb: 3,
              }}
            >
              <CardHeader title="Anzeige" subheader="Darstellung und Theme" />
              <Divider />
              <CardContent>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mode === "dark"}
                        onChange={(event) => setMode(event.target.checked ? "dark" : "light")}
                      />
                    }
                    label="Darkmode aktivieren"
                  />
                </FormGroup>
              </CardContent>
            </Card>

            <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 8px 28px rgba(0,0,0,0.06)",
                  mb: 3,
                }}
            >
              <CardHeader title="Rechnungsversand" subheader="Einstellungen zum automatischen Versand von Rechnungen" />
              <Divider />
              <CardContent>
                <FormGroup>
                  <FormControlLabel
                      control={
                        <Checkbox
                            checked={!!currentProfil?.invoiceCopyByMail}
                            onChange={(e) => setCurrentProfil((prev) => ({ ...prev, invoiceCopyByMail: e.target.checked }))}
                        />
                      }
                      label="Rechnungskopie per E-Mail erhalten"
                  />
                </FormGroup>
              </CardContent>
            </Card>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 8px 28px rgba(0,0,0,0.06)",
                mb: 3,
              }}
            >
              <CardHeader
                title="Eigener Mailserver"
                subheader="Optional: Wenn Sie Rechnungen über Ihren eigenen Mailserver versenden möchten"
              />
              <Divider />
              <CardContent>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useCustomMailServer}
                        onChange={handleUseCustomMailServerChange}
                      />
                    }
                    label="Eigenen Mailserver verwenden"
                  />
                </FormGroup>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mailserver"
                      value={settingsData.mailSettings.smtpHost || ""}
                      onChange={(e) =>
                        handleInputChange("smtpHost", e.target.value)
                      }
                      fullWidth
                      disabled={!useCustomMailServer}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Port"
                      type="number"
                      value={settingsData.mailSettings.smtpPort || ""}
                      onChange={(e) =>
                        handleInputChange("smtpPort", e.target.value)
                      }
                      fullWidth
                      disabled={!useCustomMailServer}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Absenderadresse"
                      value={settingsData.mailSettings.smtpFromAddress || ""}
                      onChange={(e) =>
                        handleInputChange("smtpFromAddress", e.target.value)
                      }
                      fullWidth
                      disabled={!useCustomMailServer}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Benutzername"
                      value={settingsData.mailSettings.smtpUsername || ""}
                      onChange={(e) =>
                        handleInputChange("smtpUsername", e.target.value)
                      }
                      fullWidth
                      disabled={!useCustomMailServer}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Passwort"
                      type="password"
                      value={settingsData.mailSettings.smtpPassword || ""}
                      onChange={(e) =>
                        handleInputChange("smtpPassword", e.target.value)
                      }
                      fullWidth
                      disabled={!useCustomMailServer}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!settingsData.mailSettings.smtpSsl}
                          onChange={(e) =>
                            handleInputChange("smtpSsl", e.target.checked)
                          }
                          disabled={!useCustomMailServer}
                        />
                      }
                      label="SSL/TLS verwenden"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 8px 28px rgba(0,0,0,0.06)",
                mb: 3,
              }}
            >
              <CardHeader
                title="Datensicherung & Export"
                subheader="Exportieren Sie alle Daten und Belege für externe Backups oder den Umzug zu Albert"
              />
              <Divider />
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Mit dieser Funktion erstellen Sie ein vollständiges ZIP-Archiv Ihres Mandanten.
                  Das Archiv enthält das Manifest mit Prüfsummen, alle Stammdaten (Profil, Kunden, Lieferanten,
                  Artikel), Buchungsdaten (Rechnungen, Dauerrechnungen, Aufträge, Ausgaben, Einnahmen, Banking, Anlagen,
                  Projekte, Zeiterfassungen, Mitarbeiter) sowie alle hochgeladenen Belege und PDF-Dokumente.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleDownloadBackup}
                    disabled={exportLoading}
                    startIcon={
                      exportLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <FileDownloadRoundedIcon />
                      )
                    }
                    sx={{
                      bgcolor: "#2e7d32",
                      "&:hover": { bgcolor: "#1b5e20" },
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  >
                    {exportLoading
                      ? "Erstelle Datensicherung..."
                      : "Komplett-Backup herunterladen (.zip)"}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                disabled={saveLoading}
                sx={{
                  bgcolor: "orange",
                  "&:hover": { bgcolor: "#cc7a00" },
                  borderRadius: 2,
                  px: 3,
                  color: "#000",
                }}
              >
                {saveLoading ? "Speichern..." : "Speichern"}
              </Button>
            </Box>
          </Box>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}
